/**
 * Document Correlation View
 *
 * Shows cross-document correlations (invoice-payment matches, duplicates, discrepancies).
 * Uses the correlation router to find and display matches.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import {
  ArrowRightLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Search,
  Link2,
  FileText,
  Receipt,
  CreditCard,
  Copy,
  Eye,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
} from "lucide-react";

interface CorrelationMatch {
  id: number;
  sourceDocumentId: number;
  targetDocumentId: number | null;
  correlationType: string;
  matchConfidence: number;
  matchedOn: {
    amount?: boolean;
    date?: boolean;
    vendor?: boolean;
    reference?: boolean;
    iban?: boolean;
  } | null;
  hasDiscrepancy: boolean | null;
  discrepancy?: {
    type: "amount" | "date" | "vendor" | "other";
    sourceValue: string;
    targetValue: string;
    explanation?: string;
  } | null;
  isReviewed: boolean | null;
  isConfirmed?: boolean | null;
}

const correlationTypeLabels: Record<string, string> = {
  invoice_payment: "Invoice → Payment",
  receipt_expense: "Receipt → Expense",
  contract_transaction: "Contract → Transaction",
  statement_voucher: "Statement → Voucher",
  duplicate: "Duplicate",
  related: "Related",
};

const correlationTypeIcons: Record<string, typeof Link2> = {
  invoice_payment: ArrowRightLeft,
  receipt_expense: Receipt,
  duplicate: Copy,
  related: Link2,
};

export function DocumentCorrelationView() {
  const { selectedCompanyId } = useCompany();
  const [activeTab, setActiveTab] = useState("matches");

  // Get correlations
  const {
    data: correlationsData,
    isLoading,
    refetch,
  } = trpc.correlation.list.useQuery(
    {
      companyId: selectedCompanyId!,
      limit: 100,
    },
    { enabled: !!selectedCompanyId }
  );

  // Extract correlations array from paginated response
  const correlations = correlationsData?.correlations;

  // Get correlation stats
  const { data: stats } = trpc.correlation.getStats.useQuery(
    {
      companyId: selectedCompanyId!,
    },
    { enabled: !!selectedCompanyId }
  );

  // Run correlation mutation
  const runCorrelationMutation = trpc.correlation.runAnalysis.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  // Review correlation mutation
  const reviewMutation = trpc.correlation.review.useMutation({
    onSuccess: () => {
      refetch();
    },
  });

  const handleRunCorrelation = () => {
    if (!selectedCompanyId) return;
    runCorrelationMutation.mutate({
      companyId: selectedCompanyId,
      saveResults: true,
    });
  };

  const handleReview = (correlationId: number, isConfirmed: boolean) => {
    reviewMutation.mutate({
      correlationId,
      isConfirmed,
    });
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return "text-success";
    if (confidence >= 50) return "text-warning";
    return "text-muted-foreground";
  };

  const getMatchStrengthLabel = (
    matchedOn?: {
      amount?: boolean;
      date?: boolean;
      vendor?: boolean;
      reference?: boolean;
      iban?: boolean;
    } | null
  ) => {
    if (!matchedOn) return "No matches";
    const matches = [];
    if (matchedOn.amount) matches.push("Amount");
    if (matchedOn.date) matches.push("Date");
    if (matchedOn.vendor) matches.push("Vendor");
    if (matchedOn.reference) matches.push("Reference");
    if (matchedOn.iban) matches.push("IBAN");
    return matches.length > 0 ? matches.join(", ") : "Partial match";
  };

  if (!selectedCompanyId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground text-center">
            Select a company to view correlations
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">
                {stats.totalCorrelations}
              </div>
              <p className="text-xs text-muted-foreground">
                Total Correlations
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-success">
                {stats.confirmed}
              </div>
              <p className="text-xs text-muted-foreground">Confirmed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-warning">
                {stats.totalCorrelations - stats.reviewed}
              </div>
              <p className="text-xs text-muted-foreground">Pending Review</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">
                {stats.withDiscrepancies}
              </div>
              <p className="text-xs text-muted-foreground">Discrepancies</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5" />
                Document Correlations
              </CardTitle>
              <CardDescription>
                Cross-document matching: invoices to payments, receipts to
                expenses
              </CardDescription>
            </div>
            <Button
              onClick={handleRunCorrelation}
              disabled={runCorrelationMutation.isPending}
            >
              {runCorrelationMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Run Correlation
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="matches" className="flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Matches
              </TabsTrigger>
              <TabsTrigger value="pending" className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Pending Review
              </TabsTrigger>
              <TabsTrigger
                value="discrepancies"
                className="flex items-center gap-2"
              >
                <AlertTriangle className="h-4 w-4" />
                Discrepancies
              </TabsTrigger>
            </TabsList>

            <TabsContent value="matches" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !correlations?.length ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No correlations found</p>
                  <p className="text-sm">
                    Process some documents first, then run correlation analysis
                  </p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {correlations.map((correlation: CorrelationMatch) => {
                      const TypeIcon =
                        correlationTypeIcons[correlation.correlationType] ||
                        Link2;
                      return (
                        <div
                          key={correlation.id}
                          className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-[var(--primary)]/10">
                              <TypeIcon className="h-5 w-5 text-[var(--primary)]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline">
                                  {correlationTypeLabels[
                                    correlation.correlationType
                                  ] || correlation.correlationType}
                                </Badge>
                                <span
                                  className={`text-sm font-medium ${getConfidenceColor(correlation.matchConfidence)}`}
                                >
                                  {correlation.matchConfidence}% confidence
                                </span>
                                {correlation.isConfirmed === true && (
                                  <Badge className="bg-success">
                                    Confirmed
                                  </Badge>
                                )}
                                {correlation.isConfirmed === false && (
                                  <Badge variant="destructive">Rejected</Badge>
                                )}
                              </div>

                              <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                <FileText className="h-4 w-4" />
                                <span>Doc #{correlation.sourceDocumentId}</span>
                                <ArrowRightLeft className="h-4 w-4" />
                                <span>Doc #{correlation.targetDocumentId}</span>
                              </div>

                              <p className="mt-1 text-xs text-muted-foreground">
                                Matched on:{" "}
                                {getMatchStrengthLabel(correlation.matchedOn)}
                              </p>

                              {correlation.discrepancy && (
                                <div className="mt-2 p-2 rounded bg-warning/10 border border-[var(--warning)]/30">
                                  <p className="text-xs text-warning">
                                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                                    {correlation.discrepancy.type}:{" "}
                                    {correlation.discrepancy.sourceValue} vs{" "}
                                    {correlation.discrepancy.targetValue}
                                  </p>
                                </div>
                              )}
                            </div>

                            {!correlation.isReviewed && (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-success hover:bg-success/10"
                                  onClick={() =>
                                    handleReview(correlation.id, true)
                                  }
                                  disabled={reviewMutation.isPending}
                                >
                                  <ThumbsUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
                                  onClick={() =>
                                    handleReview(correlation.id, false)
                                  }
                                  disabled={reviewMutation.isPending}
                                >
                                  <ThumbsDown className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="pending" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {correlations?.filter(
                      (c: CorrelationMatch) => !c.isReviewed
                    ).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>All correlations reviewed</p>
                      </div>
                    ) : (
                      correlations
                        ?.filter((c: CorrelationMatch) => !c.isReviewed)
                        .map((correlation: CorrelationMatch) => {
                          const TypeIcon =
                            correlationTypeIcons[correlation.correlationType] ||
                            Link2;
                          return (
                            <div
                              key={correlation.id}
                              className="p-4 rounded-lg border bg-warning/10 border-[var(--warning)]/30"
                            >
                              <div className="flex items-start gap-3">
                                <TypeIcon className="h-5 w-5 text-warning mt-0.5" />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline">
                                      {
                                        correlationTypeLabels[
                                          correlation.correlationType
                                        ]
                                      }
                                    </Badge>
                                    <span className="text-sm font-medium">
                                      {correlation.matchConfidence}% confidence
                                    </span>
                                  </div>
                                  <p className="mt-1 text-sm text-muted-foreground">
                                    Doc #{correlation.sourceDocumentId} ↔ Doc #
                                    {correlation.targetDocumentId}
                                  </p>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-success"
                                    onClick={() =>
                                      handleReview(correlation.id, true)
                                    }
                                  >
                                    <ThumbsUp className="h-4 w-4 mr-1" />
                                    Confirm
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive"
                                    onClick={() =>
                                      handleReview(correlation.id, false)
                                    }
                                  >
                                    <ThumbsDown className="h-4 w-4 mr-1" />
                                    Reject
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>

            <TabsContent value="discrepancies" className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {correlations?.filter(
                      (c: CorrelationMatch) => c.hasDiscrepancy || c.discrepancy
                    ).length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No discrepancies found</p>
                        <p className="text-sm">
                          All matched documents have consistent data
                        </p>
                      </div>
                    ) : (
                      correlations
                        ?.filter(
                          (c: CorrelationMatch) =>
                            c.hasDiscrepancy || c.discrepancy
                        )
                        .map((correlation: CorrelationMatch) => (
                          <div
                            key={correlation.id}
                            className="p-4 rounded-lg border bg-destructive/5 border-destructive/20"
                          >
                            <div className="flex items-start gap-3">
                              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="font-medium">
                                  {correlation.discrepancy?.type || "Value"}{" "}
                                  Mismatch
                                </p>
                                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">
                                      Document #{correlation.sourceDocumentId}
                                    </p>
                                    <p className="font-mono bg-muted px-2 py-1 rounded">
                                      {correlation.discrepancy?.sourceValue ||
                                        "—"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground mb-1">
                                      Document #{correlation.targetDocumentId}
                                    </p>
                                    <p className="font-mono bg-muted px-2 py-1 rounded">
                                      {correlation.discrepancy?.targetValue ||
                                        "—"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    )}
                  </div>
                </ScrollArea>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

export default DocumentCorrelationView;
